import { Loading } from "@components/common/Loading";
import OfferForm from "@components/ui/offer/form";
import { useGetFormBuilderAccessToken } from "@hooks/useGetFormBuilderAccessToken";
import { ArrowBack } from "@mui/icons-material";
import { Box, Button, Container, Grid, Paper, alpha } from "@mui/material";
import { DASHBOARD_PATH } from "@routes/path";
import { generateUUID } from "@services/api/authentication/generate-uuid/generateUUID";
import { getOffer } from "@services/api/offer/get/get";
import { useAuth } from "@services/authentication/auth.context";
import { buildOfferProps } from "@services/offer/buildOfferProps";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import translations from "./translations.json";
import { createOffer } from "@services/api/offer/create/create";
type SupportedLanguage = keyof typeof translations;

// Couleurs du thème sombre
const DARK_BG = '#0f1319';
const DARK_CARD_BG = '#161b22';
const DARK_BORDER = '#1e2638';

const CreateOffer = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { language } = useLanguage();
    const content = translations[language as SupportedLanguage] || translations.en;
    const { user } = useAuth();

    const { loading: loadingFormBuilderAccessToken, formBuilderAccessToken } = useGetFormBuilderAccessToken(["recruiter", "offer", "offer-category"]);

    const [defaultValue, setDefaultValue] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const buildDefaultProps = async () => {
        const uuid = await generateUUID();
        const props = buildOfferProps({
            uuid,
            recruiter: user?.userIDFromRelatedFormBuilder
        });
        setDefaultValue(props);
    };

    const handleAfterSubmit = async (data: any) => {
        setLoading(true);
        try {
            await createOffer(data)
            if (!location.state?.offerId) {
                await buildDefaultProps();
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const generateDefaultValue = async () => {
            if (location.state?.offerId) {
                const offer = await getOffer({ id: location.state?.offerId });
                if (offer?.data?.offerFormBuilder?.props) {
                    setDefaultValue(offer?.data?.offerFormBuilder?.props);
                } else {
                    buildDefaultProps();
                }
            } else {
                buildDefaultProps();
            }
        };
        generateDefaultValue();
    }, [location.state?.offerId]);

    return (
        <Box sx={{ backgroundColor: DARK_BG, minHeight: '100vh', py: 6, color: '#ffffff' }}>
            <Container maxWidth="xl">
                
                {/* Bouton de retour stylisé façon Theme Sombre */}
                <Button 
                    startIcon={<ArrowBack />} 
                    onClick={() => navigate(DASHBOARD_PATH)}
                    sx={{ 
                        mb: 4,
                        px: 2.5,
                        py: 1,
                        borderRadius: '8px',
                        backgroundColor: alpha('#ffffff', 0.05),
                        color: '#ffffff',
                        border: `1px solid ${DARK_BORDER}`,
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: alpha('#ffffff', 0.1),
                            borderColor: alpha('#ffffff', 0.2),
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    {content.back}
                </Button>
                
                {/* Grid du Formulaire */}
                <Grid 
                    container 
                    spacing={3} 
                    justifyContent="center"
                >
                    {loadingFormBuilderAccessToken && (
                        <Grid item xs={12} display="flex" justifyContent="center" sx={{ py: 8 }}>
                            <Loading />
                        </Grid>
                    )}
                    
                    {!loadingFormBuilderAccessToken && (
                        <Grid item xs={12} md={10} lg={8}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2.5, sm: 4 },
                                    borderRadius: '12px',
                                    backgroundColor: DARK_CARD_BG,
                                    border: `1px solid ${DARK_BORDER}`,
                                    color: '#ffffff',
                                }}
                            >
                                <OfferForm 
                                    key={formBuilderAccessToken} 
                                    onSubmit={handleAfterSubmit}
                                    defaultValue={defaultValue}
                                    successMessage={location.state?.offerId ? content.updated : content.created}
                                    formBuilderAccessToken={formBuilderAccessToken} 
                                    loading={loading}
                                />
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Container>
        </Box>
    );
};

export default CreateOffer;